import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Switch from "@material-ui/core/Switch";
import EditIcon from "@material-ui/icons/Edit";
import LaunchIcon from "@material-ui/icons/Launch";
import clsx from "clsx";
import React, {PropsWithChildren, ReactNode, useState} from "react";
import DataRow from "view/components/DataRow";
import FfairPaper from "view/components/FfairPaper";
import UploadIcon from "view/images/UploadIcon";
import LockIcon from "@material-ui/icons/Lock";
import DotsMenu from "view/components/DotsMenu";
import ClipboardButton from "view/components/ClipboardButton";
import camelCase from "lodash.camelcase";
import {logoAcceptTypes} from "tools/file/createLogoUploadListener";
import {Button} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import {useLocalizedNameTranslate} from "hooks/useLocalizedNameTranslate";
import EditModal from "./EditModal";
import {
  EditModalProps,
  Setting,
  SettingsTableProps,
  SettingTypes,
  SettingValues,
  TextSettings,
} from "./types";
import useStyles from "./styles";
import FlexBox from "../Layout/FlexBox";

const SettingsList = <T extends SettingValues>({
  title,
  editTitle,
  settings,
  onUpdate,
  values,
  className,
  withoutPaper,
  style,
  button,
  buttons,
  tooltipPosition,
  customTitle,
}: PropsWithChildren<SettingsTableProps<T>>) => {
  const styles = useStyles();
  const t = useLocalizedNameTranslate();
  const [state, setState] = useState<Setting<T> | null | undefined>(null);
  const handleClose = () => setState(null);
  const renderTitle = ({title}: Setting<T>): string => {
    return `${title}`;
  };

  const renderValue = ({value}: Setting<T>): ReactNode => value;

  const renderButton = (setting: Setting<T>) => {
    const renderLinkButton = () => {
      if (setting.type !== "link") {
        throw new Error("Error type (expected link)");
      }

      const {link} = setting;
      return (
        <a target="_blank" href={link || ""} rel="noreferrer">
          <IconButton className={`${styles.button}`}>
            <LaunchIcon />
          </IconButton>
        </a>
      );
    };

    const renderClipboardButton = () => {
      if (setting.type !== "clipboard") {
        throw new Error("Error type (expected text)");
      }

      return (
        <ClipboardButton value={setting.value} className={styles.button} />
      );
    };

    const renderBlockedButton = () => {
      return (
        <IconButton disabled className={styles.button}>
          <LockIcon />
        </IconButton>
      );
    };

    const renderFileButton = () => {
      if (setting.type !== "file") {
        throw new Error("Error type (expected file)");
      }
      const id = setting?.id || "upload-photo";

      return (
        <label htmlFor={id}>
          <input
            style={{display: "none"}}
            id={id}
            name={id}
            type="file"
            accept={logoAcceptTypes.join(", ")}
            onChange={setting.onChangeFile}
          />
          <IconButton className={styles.button} component="span">
            <UploadIcon className={styles.uploadIcon} />
          </IconButton>
        </label>
      );
    };
    const renderNumberButton = () => {
      if (setting.type !== "number") {
        throw new Error("Error type (expected file)");
      }
      return (
        <IconButton className={styles.button} onClick={() => setState(setting)}>
          <EditIcon />
        </IconButton>
      );
    };

    const renderTextButton = () => {
      if (setting.type !== "text") {
        throw new Error("Error type (expected text)");
      }
      return (
        <IconButton className={styles.button} onClick={() => setState(setting)}>
          <EditIcon />
        </IconButton>
      );
    };
    const renderRadio = () => {
      if (setting.type !== "radio") {
        throw new Error("Error type (expected radio)");
      }
      return (
        <IconButton className={styles.button} onClick={() => setState(setting)}>
          <EditIcon />
        </IconButton>
      );
    };
    const renderSwitch = () => {
      if (setting.type !== "switch") {
        throw new Error("Error type (expected switch)");
      }

      const handleChange = async (
        e: React.ChangeEvent<HTMLInputElement>,
        checked: boolean
      ) => {
        onUpdate({[setting.name]: checked} as T);
      };
      return (
        <div className={styles.switchContainer}>
          <Switch
            color="primary"
            name={setting.name}
            disabled={setting?.disabled}
            checked={!!setting.value}
            onChange={handleChange}
          />
        </div>
      );
    };

    const renderMultiSelectButton = () => {
      if (setting.type !== "multiselect") {
        throw new Error("Error type (expected multiselect)");
      }
      return (
        <IconButton className={styles.button} onClick={() => setState(setting)}>
          <EditIcon />
        </IconButton>
      );
    };

    const renderDotsMenu = () => {
      if (setting.type !== "dots-menu") {
        throw new Error("Error type (expected file)");
      }
      const {options} = setting;
      return <DotsMenu id={camelCase(title)} options={options} />;
    };

    const getRenderButtonFunction = (type: SettingTypes["type"]) => {
      switch (type) {
        case "file":
          return renderFileButton;
        case "link":
          return renderLinkButton;
        case "clipboard":
          return renderClipboardButton;
        case "read-only":
          return () => <div />;
        case "blocked":
          return renderBlockedButton;
        case "dots-menu":
          return renderDotsMenu;
        case "switch":
          return renderSwitch;
        case "multiselect":
          return renderMultiSelectButton;
        case "number":
          return renderNumberButton;
        case "radio":
          return renderRadio;
        default:
          return renderTextButton;
      }
    };

    const renderButton = getRenderButtonFunction(setting.type);

    return <div id={setting.buttonId}>{renderButton()}</div>;
  };

  const renderRows = () => {
    return settings.map((setting: Setting<T>, idx: number) =>
      setting.type === "custom" ? (
        setting.render(onUpdate)
      ) : (
        <DataRow
          key={setting.title as string}
          label={renderTitle(setting)}
          value={renderValue(setting)}
          actionPanel={renderButton(setting)}
          last={idx === settings.length - 1}
          tooltipText={setting.tooltipText as string}
          tooltipPosition={tooltipPosition}
        />
      )
    );
  };

  const renderModal = () => {
    if (!state) {
      return null;
    }

    const explicitTitle = state.modalTitle && String(state.modalTitle);
    const name = String(editTitle ? state.title : title);
    const modalTitle =
      explicitTitle ||
      (t ? t("common.modal.edit_title", {name}) : `Edit ${name}`);

    return (
      <EditModal
        onSubmit={onUpdate}
        defaultValues={values}
        handleClose={handleClose}
        title={modalTitle}
        infoMessage={state.infoMessage}
        fields={(state as TextSettings<T>).fields}
        multiselect={state as EditModalProps<T>["multiselect"]}
        localisation={state.localisation}
      />
    );
  };

  const renderTable = () => {
    return (
      <>
        {renderModal()}
        <FlexBox alignCenter justifyContent="space-between" paddingRight={3}>
          {customTitle ?? (
            <Typography variant="subtitle2" className={styles.settingsTitle}>
              {title}
            </Typography>
          )}
          {(button || buttons?.length) && (
            <FlexBox alignCenter gap={1}>
              {[...(button ? [button] : []), ...(buttons ?? [])].map(
                (headerButton, index) => (
                  <Button
                    key={headerButton.id ?? index}
                    id={headerButton.id}
                    color="primary"
                    variant={headerButton.variant || "contained"}
                    onClick={headerButton.action}
                    startIcon={
                      headerButton.startIcon ?? (
                        <AddIcon className={headerButton.className} />
                      )
                    }
                    endIcon={headerButton.endIcon}
                  >
                    {headerButton.title}
                  </Button>
                )
              )}
            </FlexBox>
          )}
        </FlexBox>
        <Box marginTop={2}>{renderRows()}</Box>
      </>
    );
  };

  if (withoutPaper) {
    return renderTable();
  }

  return (
    <FfairPaper className={clsx(styles.paper, className)} style={style}>
      {renderTable()}
    </FfairPaper>
  );
};

export default SettingsList;
